import React, { useState } from 'react';
import classNames from 'classnames';
import { default as RDatepicker, registerLocale } from 'react-datepicker';
import { enGB, fr } from 'date-fns/locale';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import JumpLeft from '../../assets/double-chevron-left.svg';
import ChevronRight from '../../assets/chevron-left.svg';
import { useTranslation } from 'next-i18next';
import { PopoverMenu } from '../index';

registerLocale('en-GB', enGB);
registerLocale('fr', fr);

const Datepicker = ({
  name,
  value,
  onChange,
  className,
  placeholder,
  minDate,
  selectsRange,
  dateFormat = 'dd/MM/yyyy',
  popoverRef,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { t } = useTranslation('common');

  const _onChange = (value) => {
    if (selectsRange) {
      if (value[0] && value[1]) {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }

    const event = { target: { name, value: value } };
    if (onChange) {
      onChange(event);
    }
  };

  const prevent = (e, fn) => {
    e.preventDefault();
    fn(e);
  };

  const customHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled,
  }) => (
    <div className="flex justify-between relative">
      <div className="flex gap-4">
        <button
          onClick={(e) => prevent(e, decreaseYear)}
          disabled={prevYearButtonDisabled}
          aria-label={t('general.$previous')}
        >
          <JumpLeft className={classNames(prevYearButtonDisabled ? 'fill-white/25' : 'fill-white')} />
        </button>

        <button
          onClick={(e) => prevent(e, decreaseMonth)}
          disabled={prevMonthButtonDisabled}
          aria-label={t('general.$previous')}
        >
          <ChevronRight className={classNames(prevMonthButtonDisabled ? 'fill-white/25' : 'fill-white', '')} />
        </button>
      </div>
      <span className="react-datepicker__current-month">
        {monthDate.toLocaleString(router.locale, {
          month: 'long',
          year: 'numeric',
        })}
      </span>
      <div className="flex gap-4">
        <button
          onClick={(e) => prevent(e, increaseMonth)}
          disabled={nextMonthButtonDisabled}
          aria-label={t('general.$next')}
        >
          <ChevronRight
            className={classNames(nextMonthButtonDisabled ? 'fill-white/25' : 'fill-white', 'rotate-180')}
          />
        </button>
        <button
          onClick={(e) => prevent(e, increaseYear)}
          disabled={nextYearButtonDisabled}
          aria-label={t('general.$next')}
        >
          <JumpLeft className={classNames(nextYearButtonDisabled ? 'fill-white/25' : 'fill-white', 'rotate-180')} />
        </button>
      </div>
      <div className="w-full h-[1px] bg-white/10 absolute left-0 -bottom-2"></div>
    </div>
  );

  const valueStr = selectsRange
    ? props.startDate || props.endDate
      ? `${(props.startDate && format(props.startDate, dateFormat)) || ''} - ${
          (props.endDate && format(props.endDate, dateFormat)) || ''
        }`
      : null
    : value && format(value, dateFormat);

  return (
    <PopoverMenu
      open={open}
      title={valueStr ? valueStr : placeholder}
      titleClass={!valueStr && placeholder && 'placeholder'}
      onOpen={() => setOpen(true)}
      className={classNames(className, 'datepicker', value && 'selected')}
      ref={popoverRef}
      {...props}
    >
      <RDatepicker
        selected={value}
        onChange={_onChange}
        locale={router.locale}
        startDate={new Date()}
        minDate={minDate}
        placeholderText={placeholder}
        formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 1)}
        dateFormat={dateFormat}
        name={name}
        showDisabledMonthNavigation
        renderCustomHeader={customHeader}
        inline
        selectsRange={selectsRange}
        {...props}
      />
    </PopoverMenu>
  );
};

export default Datepicker;
